/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { Suspense, lazy } from 'react';
import { WidgetFallback } from '@mummssoftware/common-ui';
import { BrowserRouter } from 'react-router-dom';
import { Pim, auth } from '@mummssoftware/utils';
import { DEFAULT_LOCALE } from './i18n';
import Providers from './Providers';
import {
  PatientsListingContext,
  PatientsListingContextProvider,
} from './context';
import App from './containers/App';
import defaults from './translations/en.json';
const translations: NonNullable<mumms.JSONObject> = defaults;

type PatientsListingProps = {
  /* whether the app is off or online */
  online: boolean;
  keycloak: Keycloak.KeycloakInstance;
  theme?: string | null;
  agency?: string | null;
  locale?: keyof mumms.translations;
  /*  controls whether certain elements (i.e. the top bar) display */
  embedded?: boolean;
  /* optional instance of pim, otherwise instantiates its own */
  pim?: Pim;
  /* if embedded title of the app or default Patient vitals title */
  appTitle?: string;
  /* pass currentUser if already retrieved to avoid excessive api calls */
  currentUser?: mumms.User;
  /* pass patientsList if already retrieved to avoid excessive api calls */
  patientsList?: any;
  /* if the component is embedded onClick to toggle PatientsListing to full-size */
  onClick?: () => void;
  onRemove?: () => void;
  doNotShow?: boolean;
  /* dashboard */
  smallWidget?: boolean;
  /* dashboard key forcing React to render */
  key?: any;
  /* current user siteId - needed for agencies with multiple siteId */
  siteId?: string | null;
  /* If watermark should display on top of the widget. */
  /* If readonly = true, the widget will not be interactive */
  watermark?: {
    show: boolean;
    readOnly: boolean;
  };
  viewMode?: mumms.viewMode;
  printConfig?: mumms.printConfig;
  onWidgetReady?: (arg: mumms.ComponentName) => void;
};

const ListingContext = lazy(() =>
  import('./containers/Context/ListingContext'),
);
interface PatientsListingState {
  context: PatientsListingContext | null;
}

const setError = (res: Response, type?: string) => ({
  appError: {
    status: res?.status || null,
    statusText: res?.statusText || null,
    type,
  },
});

const Content = React.memo(
  ({
    context,
    embedded,
    smallWidget,
  }: PatientsListingState & {
    embedded?: boolean;
    smallWidget?: boolean;
  }) => {
    if (context === null) {
      return (
        <WidgetFallback
          embedded={embedded}
          appTitle={translations.title as string}
          smallWidget={smallWidget}
        />
      );
    }
    return (
      <PatientsListingContextProvider value={context}>
        <Suspense
          fallback={
            <WidgetFallback
              embedded={embedded}
              appTitle={translations.title as string}
              smallWidget={smallWidget}
            />
          }
        >
          <ListingContext>
            <App
              embedded={embedded}
              currentUser={context.currentUser}
              patientsList={context.patientsList}
              online={context.online}
              logout={context.keycloak && context.keycloak.logout}
              viewMode={context.viewMode}
              onWidgetReady={context.onWidgetReady}
              appTitle={context.appTitle}
              smallWidget={context.smallWidget}
              onClick={context.onClick}
              onRemove={context.onRemove}
              doNotShow={context.doNotShow}
            />
          </ListingContext>
        </Suspense>
      </PatientsListingContextProvider>
    );
  },
);

Content.displayName = 'MainContent';

class PatientsListing extends React.PureComponent<
  PatientsListingProps,
  PatientsListingState
> {
  pim: Pim;

  agency?: string;

  constructor(props: PatientsListingProps) {
    super(props);

    const { keycloak, agency } = props;
    // avoid logic is agency is passed as props
    if (agency) {
      this.agency = agency;
    } else {
      // if the agency has not been passed as props and this could be an admin check if in url
      // if ADMIN agency in the URL
      const query = new URLSearchParams(window.location.search);
      const adminAgency = query.get('agency') as string;
      if (adminAgency) {
        this.agency = adminAgency;
      } else {
        // NON ADMIN look for agency in keycloack
        const parsedAgency = auth.getLdapDn(keycloak.idTokenParsed);
        this.agency = auth.getAgency(parsedAgency);
      }
    }
    this.pim =
      props.pim ||
      new Pim({
        url: process.env.REACT_APP_PIM_BASEURL as string,
        keycloak,
        agency: this.agency,
        pgRestUrl: process.env.REACT_APP_PGREST_BASEURL as string,
      });

    this.state = {
      context: null,
    };
  }

  getInitialData = async () => {
    const {
      currentUser: externalUser,
      patientsList: externalPatientsList,
    } = this.props;
    // if provided a user via props, just pass it along to avoid extra call
    // if provided a patientsList via props, just pass it along to avoid extra call
    const [currentUser, patientsList] = await Promise.all([
      !externalUser
        ? this.pim.getUserInfo(this.agency || '')
        : Promise.resolve(externalUser),
      !externalPatientsList
        ? this.pim.getAllPatients()
        : Promise.resolve(externalPatientsList),
    ]);

    // if currentUser error will be caught beforehand in dashboard
    if (currentUser instanceof Response) {
      throw new Error(currentUser.statusText);
    }

    let patientsListState: any;

    if (patientsList) {
      if (patientsList instanceof Response) {
        patientsListState = {
          ...setError(patientsList, 'patientsList'),
        };
      } else {
        patientsListState = { patientsList };
      }
    }

    this.setState({
      context: {
        keycloak: this.props.keycloak,
        currentUser,
        locale: this.props.locale || DEFAULT_LOCALE,
        agency: (this.props.agency || currentUser.currentHospiceBin) as string,
        ...patientsListState,
        appTitle: this.props.appTitle,
        smallWidget: this.props.smallWidget,
        watermark: this.props.watermark,
        viewMode: this.props.viewMode,
        onClick: this.props.onClick,
        onRemove: this.props.onRemove,
        doNotShow: this.props.doNotShow,
        onWidgetReady: this.props.onWidgetReady,
      },
    });
  };

  componentDidMount() {
    this.getInitialData();
  }

  render() {
    const { context } = this.state;
    const {
      embedded,
      theme,
      locale = DEFAULT_LOCALE,
      smallWidget,
    } = this.props;
    return (
      <Providers locale={locale} key={locale} theme={theme}>
        <BrowserRouter>
          <Content
            context={context}
            embedded={embedded}
            smallWidget={smallWidget}
          />
        </BrowserRouter>
      </Providers>
    );
  }
}

export default PatientsListing;
